import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import {
  FiArrowRight, FiMail, FiPhone, FiMapPin,
} from 'react-icons/fi';
import Button from '../shared/Button';


// Design Tokens
const colors = {
  primary: '#2563EB',
  primaryDark: '#1D4ED8',
  primaryLight: '#3B82F6',
  secondary: '#10B981',
  error: '#DC2626',
  success: '#10B981',
  warning: '#F59E0B',
  background: '#F8FAFC',
  surface: '#FFFFFF',
  textPrimary: '#1E293B',
  textSecondary: '#64748B',
  border: '#E2E8F0',
  overlay: 'rgba(0, 0, 0, 0.5)'
};

const spacing = {
  xs: '4px',
  sm: '8px',
  md: '16px',
  lg: '24px',
  xl: '32px',
  xxl: '48px',
  xxxl: '64px'
};

const borderRadius = {
  sm: '4px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  xxl: '24px',
  full: '9999px'
};

const typography = {
  heading1: '2.5rem',
  heading2: '2rem',
  heading3: '1.5rem',
  heading4: '1.25rem',
  body: '1rem',
  small: '0.875rem',
  tiny: '0.75rem'
};

const shadows = {
  sm: '0 1px 2px 0 rgba(0, 0, 0, 0.05)',
  md: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
  lg: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
  xl: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
  inner: 'inset 0 2px 4px 0 rgba(0, 0, 0, 0.06)'
};

const breakpoints = {
  sm: '640px',
  md: '768px',
  lg: '1024px',
  xl: '1280px'
};

// Layout Components
const PageContainer = styled.div`
  max-width: 1440px;
  margin: 0 auto;
  padding: ${spacing.xxxl};
  
  @media (max-width: ${breakpoints.md}) {
    padding: ${spacing.xl};
  }
`;

const Section = styled.section`
  background: ${colors.background};
  border-radius: ${borderRadius.xxl};
  overflow: hidden;
  box-shadow: ${shadows.md};
  margin-bottom: ${spacing.xxxl};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: ${spacing.xxl};
  
  @media (max-width: ${breakpoints.lg}) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled.div`
  background: ${colors.surface};
  border-radius: ${borderRadius.xl};
  padding: ${spacing.xxl};
  box-shadow: ${shadows.sm};
  transition: all 0.3s ease;
  
  &:hover {
    box-shadow: ${shadows.md};
    transform: translateY(-2px);
  }
`;

// Typography Components
const Heading = styled.h2`
  font-size: ${typography.heading2};
  color: ${colors.textPrimary};
  font-weight: 600;
  margin-bottom: ${spacing.lg};
  line-height: 1.3;
`;

const Subheading = styled.h3`
  font-size: ${typography.heading3};
  color: ${colors.textPrimary};
  font-weight: 600;
  margin-bottom: ${spacing.md};
  line-height: 1.4;
`;

const Text = styled.p`
  font-size: ${typography.body};
  color: ${colors.textSecondary};
  line-height: 1.6;
  margin-bottom: ${spacing.md};
`;

// Contact Info Components
const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.md};
  margin-bottom: ${spacing.lg};
  
  svg {
    color: ${colors.primary};
    margin-top: 2px;
    flex-shrink: 0;
  }
`;

const Divider = styled.div`
  height: 1px;
  background: linear-gradient(90deg, transparent, ${colors.border}, transparent);
  margin: ${spacing.xl} 0;
`;

const SectionContainer = styled.div`
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 60px;
  position: relative;

  @media (max-width: 992px) {
    padding: 0 40px;
  }

  @media (max-width: 768px) {
    padding: 0 25px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 70px;
  position: relative;
  z-index: 2;

  @media (max-width: 768px) {
    margin-bottom: 50px;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: #151c48;
  margin-bottom: 20px;
  letter-spacing: -0.5px;
  position: relative;
  display: inline-block;

  &::after {
    content: '';
    position: absolute;
    bottom: -12px;
    left: 50%;
    transform: translateX(-50%);
    width: 100px;
    height: 4px;
    background: linear-gradient(90deg, #5ab9d3, #151c48);
    border-radius: 2px;
  }

  @media (max-width: 992px) {
    font-size: 2.4rem;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

// Main Component
const RelocationForm = () => {
  return (
    <PageContainer>
      <Section>
        <SectionContainer>
          <SectionHeader>
            <SectionTitle>Facilite Sua Realocação</SectionTitle>
          </SectionHeader>
        </SectionContainer>
        <Grid>
          {/* Contact Information Card */}
          <Card>
            <Heading>Assistência para Mudança em Portugal</Heading>
            <Text>
              Nossa rede de Parceiros especializados em realocação pode ajudá-lo em cada etapa da sua mudança para Portugal,
              desde encontrar moradia até navegar pela burocracia.
            </Text>

            <ContactItem>
              <FiMail size={20} />
              <div>
                <Subheading>Email</Subheading>
                <Text>contato@mudancaportugal.com</Text>
              </div>
            </ContactItem>

            <ContactItem>
              <FiPhone size={20} />
              <div>
                <Subheading>Telefone</Subheading>
                <Text>+351 123 456 789</Text>
              </div>
            </ContactItem>

            <ContactItem>
              <FiMapPin size={20} />
              <div>
                <Subheading>Endereço</Subheading>
                <Text>Av. da Liberdade, 100, Lisboa, Portugal</Text>
              </div>
            </ContactItem>

            <Divider />

            <Subheading>Para Profissionais</Subheading>
            <Text>
              Você é consultor ou empresa de realocação em Portugal? Junte-se à nossa rede de parceiros.
            </Text>
            <Button
              as={Link}
              to="/parceiros"
              primary
              style={{ margin: '0 auto' }}
            >
              Crie Sua Conta <FiArrowRight style={{ transition: 'transform 0.3s ease' }} />
            </Button>
          </Card>

          {/* Questionário Card */}
          <Card>
            <Subheading>Solicite uma Consultoria Gratuita</Subheading>
            <Text>
              Preencha nosso questionário completo para que possamos entender melhor suas necessidades
              e conectar você com os melhores especialistas em realocação para Portugal.
            </Text>

            <div style={{
              display: 'flex',
              justifyContent: 'center',
              margin: `${spacing.xxl} 0`
            }}>
              <Button
                as={Link}
                to="/relocation-questionnaire"
                primary
                size="large"
                style={{
                  padding: `${spacing.md} ${spacing.xl}`,
                  fontSize: typography.body,
                  fontWeight: 600
                }}
              >
                Preencher Questionário de Relocação <FiArrowRight />
              </Button>
            </div>

            <Text style={{
              textAlign: 'center',
              color: colors.textSecondary,
              fontSize: typography.small,
              marginTop: spacing.xl
            }}>
              Nosso questionário detalhado leva aproximadamente 5 minutos para ser preenchido e nos ajuda
              a fornecer um atendimento totalmente personalizado para sua mudança.
            </Text>
          </Card>
        </Grid>
      </Section>
    </PageContainer>
  );
};

export default RelocationForm;